/**
 * Learning Path Enroll Button
 * Handles enrollment, starting, and navigation for learning paths
 */
'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { useLoginModals } from '@/components/Login/common/LoginModalsManager';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { useLearningPathActions } from '@/hooks/useLearningPathActions';
import { isLearningPathCompleted } from '@/lib/utils/learning-path-utils';
import MaterialIcons from '../MaterialIcons/materialIcons';

interface LearningPathEnrollButtonProps {
  learningPathUid?: string;
  initialIsSubscribed?: boolean;
  initialHasStarted?: boolean;
  progressPercent?: number;
  /** When true, show Completed. When false, never show Completed. When undefined, use progressPercent >= 100 */
  isCompleted?: boolean;
  /** When true, LP final assessment is unlocked (all courses done or LP progress rules). Used for "Take Assessment" state. */
  isLearningMaterialCompleted?: boolean;
  /** When true, LP has assessment. Required for "Take Assessment" button. */
  hasAssessment?: boolean;
  navigateUrl?: string;
  variant?: 'PrimaryFillButton' | 'SecondaryFillButton' | 'OpacityButton' | 'ghost' | 'UnderlineButton' | 'ButtonContainer' | 'InlinePrimary' | 'InlineSecondary' | 'link' | 'DocSecondaryBorder' | 'DocWhiteSecondary' | 'DocsPrimaryFillButton';
  className?: string;
  buttonText?: {
    enrolling?: string;
    enrolled?: string;
    enrolledAndStarted?: string;
    notEnrolled?: string;
    takeAssessment?: string;
  };
  showIcon?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  autoNavigate?: boolean;
  autoStartAfterEnroll?: boolean;
  /** Use direct hover (no card/group) - for hero etc. Hover = Green-Shade, active = Green-Light */
  directHover?: boolean;
  children?: (props: {
    isSubscribed: boolean;
    hasStarted: boolean;
    isLoading: boolean;
    buttonText: string;
    handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  }) => React.ReactNode;
}

export default function LearningPathEnrollButton({
  learningPathUid,
  initialIsSubscribed = false,
  initialHasStarted = false,
  progressPercent,
  isCompleted: isCompletedProp,
  isLearningMaterialCompleted = false,
  hasAssessment = false,
  navigateUrl,
  variant = 'PrimaryFillButton',
  className = '',
  buttonText = {},
  showIcon = true,
  onClick,
  autoNavigate = true,
  autoStartAfterEnroll = false,
  directHover = false,
  children,
}: LearningPathEnrollButtonProps) {
  const router = useRouter();
  const { openLoginModal } = useLoginModals();
  
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a useState seeded once from isUserLoggedIn() only reflected auth state as of mount.
  const { isLoggedIn } = useAuthUser();

  const {
    isSubscribed,
    hasStarted,
    isLoading,
    enroll,
    start,
  } = useLearningPathActions({
    learningPathUid,
    initialIsSubscribed,
    initialHasStarted,
    onLoginRequired: openLoginModal,
  });

  const handleButtonClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    onClick?.(e);
    
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    
    if (!isSubscribed) {
      // User is not enrolled - enroll them first
      const enrolled = await enroll();
      if (enrolled && autoStartAfterEnroll) {
        await start();
      }
      if (enrolled && autoNavigate && navigateUrl) {
        router.push(navigateUrl);
      }
    } else if (!hasStarted) {
      // User is enrolled but hasn't started - mark as started and navigate
      await start();
      if (autoNavigate && navigateUrl) {
        router.push(navigateUrl);
      }
    } else {
      // User has already started - just navigate to resume point
      if (autoNavigate && navigateUrl) {
        router.push(navigateUrl);
      }
    }
  }, [isLoggedIn, isSubscribed, hasStarted, openLoginModal, enroll, start, autoStartAfterEnroll, autoNavigate, navigateUrl, router, onClick]);

  const isCompleted = isLearningPathCompleted(isCompletedProp, progressPercent);
  const isTakeAssessmentState =
    isSubscribed && hasAssessment && isLearningMaterialCompleted && !isCompleted;

  const getButtonText = () => {
    if (isLoading) {
      return buttonText.enrolling || 'Enrolling...';
    }
    if (isSubscribed && isCompleted) {
      return 'Completed';
    }
    if (isTakeAssessmentState) {
      return buttonText.takeAssessment || 'Take Assessment';
    }
    if (isSubscribed) {
      if (hasStarted) {
        return buttonText.enrolledAndStarted || 'Resume Learning';
      }
      return buttonText.enrolled || 'Start learning';
    }
    return buttonText.notEnrolled || 'Enroll now';
  };

  const displayText = getButtonText();

  if (children) {
    return <>{children({
      isSubscribed,
      hasStarted,
      isLoading,
      buttonText: displayText,
      handleClick: handleButtonClick,
    })}</>;
  }

  return (
    <Button
      variant={variant}
      disabled={isLoading}
      onClick={handleButtonClick}
      className={`flex items-center gap-2 min-w-[120px] justify-center ${className} ${
        isCompleted
          ? directHover
            ? 'btn-completed-direct-hover btn-icon-move'
            : 'btn-completed-fill-group-hover btn-icon-move'
          : ''
      }`}
    >
      {displayText}
      {showIcon && (
        <MaterialIcons
          iconName={isCompleted ? 'check_circle' : 'arrow_right_alt'}
          className={
            isCompleted
              ? 'text-inherit btn-icon btn-icon-history-resume !text-[20px] flex-shrink-0 leading-none'
              : 'text-inherit btn-icon !text-[20px] flex-shrink-0 leading-none transition-transform duration-300 ease-in-out group-hover:translate-x-1'
          }
        />
      )}
    </Button>
  );
}
